import {ButtonBar, DataGridActionBar, useSnackbar} from "@dain/mui-components";
import {useCallback, useEffect, useRef, useState} from "react";
import {Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useClientLocator} from "../../client/ApiClientLocator";
import UserGrid from "./UserGrid";
import {SearchButton} from "../../common/button/CommonButtons";
import {CommonTranslKey} from "../../common/CommonTranslKey";
import {UserFilterForm} from "./UserFilterForm";
import {UserFilterDto,} from "../../client/user/UserApiClient";
import {PageableResultListDto} from "../../client/pagination/PageableResultListDto";
import {SortingOrderType} from "../../client/pagination/SortingFilterDto";
import {UserDto} from "../../client/user/User";
import {PageLoadSize} from "../../common/pagination/PageableGrid";

enum SearchPanelState {
    INITIALIZED,
    IDLE,
    SEARCH_IN_PROGRESS
}

export enum FilterSearchType {
    NEW_SEARCH,
    NEXT_PAGE_SEARCH
}

export interface UserSearchPanelProps {
    actions?: React.ReactNode;
    onSelectedUser: (user?: UserDto) => void;
    onUserDoubleClick?: (userId: number) => void;
}

export const DEFAULT_USER_FILTER: UserFilterDto = {
    paging: {
        maxPageSize: PageLoadSize.INITIAL_PAGE_SIZE,
        pageToken: null,
    },
    sorting: {
        sortingCriteria: [
            { property: "person.lastName", order: SortingOrderType.ASC },
            { property: "person.firstName", order: SortingOrderType.ASC },
        ],
    },
};

export default function UserSearchPanel(props: Readonly<UserSearchPanelProps>) {
    const {actions, onSelectedUser, onUserDoubleClick} = props;

    const {userClient} = useClientLocator();
    const snackbar = useSnackbar();
    const {t} = useTranslation();

    const [panelStatus, setPanelStatus] = useState<SearchPanelState>(SearchPanelState.INITIALIZED);
    const [userFilter, setUserFilter] = useState<UserFilterDto>(DEFAULT_USER_FILTER);

    const [userResults, setUserResults] = useState<PageableResultListDto<UserDto>>({
        items: [],
        nextPageToken: null,
    });

    const [displayedUsersCount, setDisplayedUsersCount] = useState<number>(0);
    const [selectedUser, setSelectedUser] = useState<UserDto>();

    const isInitialized = useRef(false);

    function onFilterChanged(updatedFilter: UserFilterDto) {
        setUserFilter(updatedFilter);
    }

    const updatePaginatedUsers = (pageableUser: PageableResultListDto<UserDto>, userResults: PageableResultListDto<UserDto>, searchType: FilterSearchType): PageableResultListDto<UserDto> => {
        if (searchType === FilterSearchType.NEW_SEARCH) {
            return pageableUser;
        } else if (searchType === FilterSearchType.NEXT_PAGE_SEARCH) {
            return {
                items: [...userResults.items, ...pageableUser.items],
                nextPageToken: pageableUser.nextPageToken || null
            };
        }

        return {
            items: [],
            nextPageToken: null
        };
    };

    const fetchUsers = useCallback((filter: UserFilterDto, searchType: FilterSearchType) => {
            setPanelStatus(SearchPanelState.SEARCH_IN_PROGRESS);

            userClient.findByFilter(filter)
                .then((filteredUsersPage) => {
                    const updatedUsers = updatePaginatedUsers(filteredUsersPage, userResults, searchType);
                    setUserResults(updatedUsers);
                })
                .catch((error) => {
                    snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error);
                })
                .finally(() => {
                    setPanelStatus(SearchPanelState.IDLE);
                });
        },
        [userClient, snackbar, userResults, t]
    );

    const countByFilter = useCallback((filter: UserFilterDto) => {
            setPanelStatus(SearchPanelState.SEARCH_IN_PROGRESS);
            return userClient.countByFilter(filter)
                .then((filteredUsersCount) => {
                    setDisplayedUsersCount(filteredUsersCount);
                })
                .catch((error) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error))
                .finally(() => setPanelStatus(SearchPanelState.IDLE));
        },
        [userClient, snackbar, t]
    );

    function resetPagingFilter(userFilter: UserFilterDto, DEFAULT_USER_FILTER: UserFilterDto) {
        userFilter.paging = DEFAULT_USER_FILTER.paging;
    }

    const filterSearch = async () => {
        setUserResults({items: [], nextPageToken: null});
        setDisplayedUsersCount(0);

        resetPagingFilter(userFilter, DEFAULT_USER_FILTER);

        fetchUsers(userFilter, FilterSearchType.NEW_SEARCH);
        countByFilter(userFilter);
    };

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;

            fetchUsers(DEFAULT_USER_FILTER, FilterSearchType.NEW_SEARCH);
            countByFilter(DEFAULT_USER_FILTER);
        }
    }, [fetchUsers, countByFilter]);

    function onSelectionChange(user: UserDto) {
        setSelectedUser(user);
        onSelectedUser(user);
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <UserFilterForm filter={userFilter} onChange={onFilterChanged}/>
            </Grid>
            <Grid item xs={12}>
                <ButtonBar>
                    <SearchButton onClick={filterSearch}/>
                </ButtonBar>
            </Grid>
            <Grid item xs={12}>
                <Grid container>
                    {actions && (
                        <Grid item xs={12}>
                            <DataGridActionBar actions={actions}/>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        {panelStatus !== SearchPanelState.INITIALIZED && (
                            <UserGrid
                                rowsData={userResults.items}
                                totalUserCount={displayedUsersCount}

                                nextPageToken={userResults.nextPageToken}
                                userFilter={userFilter}
                                fetchUsers={fetchUsers}

                                selectedUserId={selectedUser?.id}
                                onSelectUser={onSelectionChange}
                                onUserDoubleClick={onUserDoubleClick}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}
